import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { panelUrlParams, bookmarkURL } from './helpers';
import { PublishingContext } from './contexts';
const MAX_REQUEST_COUNT = 6;
let currentRequestCount = 0;

export function useAhaPanelContents({
  panel,
  bookmarkId,
  preview,
  showLegend,
  dashboardFilter,
  filters,
  panelUnconfigured = false,
  bookmarkStartDate,
  bookmarkEndDate,
}) {
  const [contents, setContents] = useState(null);
  const { liveView, publishing } = usePublishingConfiguration();
  const shouldUseHtmlSnapshot = useHtmlSnapshot();

  let panelUrl = liveView
    ? `${window.location.pathname}/panels/${panel.id}`
    : `${bookmarkURL(panel)}.dashboard_panel`;

  if (panel.isRichText) {
    panelUrl = panel.descriptionUrl;
  }

  useEffect(() => {
    // We don't want to make any requests as we want to use whatever html
    // is already rendered to the page
    if (shouldUseHtmlSnapshot) {
      return;
    }

    // If the panel has not been configured (e.g. a release progress chart without
    // a selected release), do not try to fetch the data from the URL.
    if (panelUnconfigured) {
      return;
    }

    let isMounted = true;

    async function fetchPanelContents() {
      // Unset the contents while we're getting the updated contents
      // so that the loading animation can be shown
      if (isMounted) setContents(null);
      let res;
      try {
        // We're in a live view so we want to make a simple get request to the server
        // Which will handle dealing with the live view panel update
        if (panel.interactiveConfigurationSharedPanelUrl) {
          res = await fetch(panel.interactiveConfigurationSharedPanelUrl, {});
        } else if (liveView) {
          res = await fetch(panelUrl, {});
        } else {
          const urlParams = panelUrlParams({
            preview,
            publishing,
            showLegend,
            bookmarkId,
            bookmarkStartDate,
            bookmarkEndDate,
          });

          // Rendering a normal dashboard panel
          res = await fetch(panelUrl, {
            method: 'POST',
            headers: { 'X-CSRF-Token': window.csrfToken() },
            body: urlParams,
          });
        }
        let contents = null;
        if (res.ok && !res.redirected) {
          contents = await res.text();
        } else {
          contents =
            '<center>We could not find the content you are looking for. It may have been deleted or the URL you entered is incorrect.</center>';
        }
        // Don't perform a state update on an unmounted component which happens
        // if the component using the hook gets unmounted before the request came back
        if (isMounted) setContents(contents);
      } finally {
        currentRequestCount = currentRequestCount - 1;
      }
    }
    const tryToQueueRequests = () => {
      if (currentRequestCount < MAX_REQUEST_COUNT) {
        currentRequestCount = currentRequestCount + 1;
        fetchPanelContents();
      } else {
        setTimeout(tryToQueueRequests, 1000);
      }
    };
    tryToQueueRequests();

    return () => {
      isMounted = false;
    };
  }, [
    panelUrl,
    preview,
    dashboardFilter,
    showLegend,
    bookmarkId,
    filters,
    liveView,
    publishing,
    shouldUseHtmlSnapshot,
    panelUnconfigured,
    panel.interactiveConfigurationSharedPanelUrl,
    bookmarkStartDate,
    bookmarkEndDate,
  ]);
  return contents;
}

export function useAutoScroll(
  scrollableRef,
  { triggerAt = 50, velocity = 10, threshold = 5 } = {}
) {
  const [enabled, setEnabled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(0);
  const [initialClientY, setInitialClientY] = useState(0);
  const [thresholdPassed, setThresholdPassed] = useState(false);
  const [containerBox, setContainerBox] = useState({ top: 0, height: 0 });

  const scrollInterval = useRef(null);

  function measureContainer() {
    setContainerBox(scrollableRef.current?.getBoundingClientRect());
  }

  function clearAutoScroll() {
    clearInterval(scrollInterval.current);
    scrollInterval.current = null;
  }

  const scroll = useCallback(
    velocity => {
      scrollableRef.current.scrollBy(0, velocity);
    },
    [scrollableRef]
  );

  useEffect(() => {
    let interval;
    if (enabled && scrollDirection && thresholdPassed) {
      interval = setInterval(scroll, 10, velocity * scrollDirection);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [enabled, scrollDirection, velocity, scroll, thresholdPassed]);

  function scrollModifier(distance) {
    const pct = Math.max(triggerAt - distance, 1) / triggerAt;

    return 1 - Math.cos((pct * Math.PI) / 2);
  }

  return {
    startAutoScroll: initialY => {
      setInitialClientY(initialY);
      measureContainer();
      setEnabled(true);
      setThresholdPassed(false);
    },
    endAutoScroll: () => {
      setInitialClientY(0);
      setEnabled(false);
      setThresholdPassed(false);
      clearAutoScroll();
    },
    autoScroll: clientY => {
      if (!enabled) return;

      const { top, height } = containerBox;
      const distanceFromTop = clientY - top;
      const distanceFromBottom = top + height - clientY;

      if (distanceFromTop < triggerAt) {
        if (initialClientY - clientY > threshold) setThresholdPassed(true);
        setScrollDirection(-1 * scrollModifier(distanceFromTop));
      } else if (distanceFromBottom < triggerAt) {
        if (clientY - initialClientY > threshold) setThresholdPassed(true);
        setScrollDirection(1 * scrollModifier(distanceFromBottom));
      } else if (
        distanceFromTop > triggerAt &&
        distanceFromBottom > triggerAt
      ) {
        setScrollDirection(0);
      }
    },
  };
}

/*
 * isSharedWebpage is true whenever we are publishing / sharing
 * liveView is true when we are requesting the Dashboard with a notebook Live Update set to true
 * isSharedWebpage == true makes use use the html that exists on the page already
 * liveView makes us request the panel with the notebok token
 * isSharedWebpage == true and liveView == false means we are a normal share webpage
 * isSharedWebpage == true and liveView == true means we are a notebook live update page
 */
export function useHtmlSnapshot() {
  const { isSharedWebpage, liveView, interactiveSharedFilters } =
    usePublishingConfiguration();
  return isSharedWebpage && !liveView && !interactiveSharedFilters;
}

export function usePublishingConfiguration() {
  const context = useContext(PublishingContext);

  if (!context) {
    throw new Error(
      `usePublishingConfiguration must be used within a PublishingContextProvider`
    );
  }

  return context;
}
